import {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../../../../common/arr-drawer/ArrDrawer';
import './AanalyzerDetailsFlyout.scss';
import ArrAccordion, {
  AccordionSection,
} from '../../../../../common/arr-accordion/ArrAccordion';
import {ReactComponent as ChinaFlag} from './../../../../../../assets/images/chinaFlag.svg';
import {ReactComponent as JapanFlag} from './../../../../../../assets/images/japanFlag.svg';
import {ReactComponent as KoreaFlag} from './../../../../../../assets/images/koreaFlag.svg';
import AccordionContent from './accordion-content/AccordionContent';
import {useTranslation} from 'react-i18next';

type AnalyzerDetailsFlyoutProps = {
  isOpen: boolean;
  onClose: () => void;
  analyzerResponse?: unknown;
};

type CustomIndexAnalyzerDetailsItem = {
  analyzerID?: number;
  analyzerName?: string | null;
  analyzerKey?: string | null;
  analyzerType?: string | null;
  alphabeticalCharacters?: string | null;
  whitespaceCharacters?: string | null;
  description?: string | null;
  AnalyzerID?: number;
  AnalyzerName?: string | null;
  AnalyzerKey?: string | null;
  AnalyzerType?: string | null;
  AlphabeticalCharacters?: string | null;
  WhitespaceCharacters?: string | null;
  Description?: string | null;
};

const languages = [
  {Icon: ChinaFlag, label: 'Chinese'},
  {Icon: JapanFlag, label: 'Japanese'},
  {Icon: KoreaFlag, label: 'Korean'},
];

const getDisplayValue = (values?: string | null) => {
  if (!values) {
    return '-';
  }
  return values.replace(/^\[\s*|\s*\]$/g, '').replace(/\\(?![nrtu^])/g, '');
};

const getAnalyzerDetails = (
  response: unknown
): CustomIndexAnalyzerDetailsItem[] => {
  if (Array.isArray(response)) {
    return response as CustomIndexAnalyzerDetailsItem[];
  }

  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as {analyzers?: unknown}).analyzers)
  ) {
    return (response as {analyzers: CustomIndexAnalyzerDetailsItem[]})
      .analyzers;
  }

  return [];
};

const AnalyzerDetailsFlyout: React.FC<AnalyzerDetailsFlyoutProps> = (props) => {
  const {isOpen, onClose, analyzerResponse} = props;
  const {t} = useTranslation();

  const renderAccordionIcons = () => (
    <Box className="accordion-language-icons">
      {languages.map(({Icon, label}, index) => (
        <Box key={label} className="accordion-language-icons__content">
          <Icon />
          <Typography
            className="typo-text body4_size"
            data-testid={`analyzer-details-flyout-lang-${index}`}
          >
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const accordionSections: AccordionSection[] = useMemo(() => {
    return getAnalyzerDetails(analyzerResponse).map((item, index) => {
      const analyzerId = item.analyzerID ?? item.AnalyzerID ?? index;
      const analyzerName =
        item.analyzerName ??
        item.AnalyzerName ??
        item.analyzerKey ??
        item.AnalyzerKey ??
        '';
      const description = item.description ?? item.Description ?? '';
      const alphabeticalCharacters =
        item.alphabeticalCharacters ?? item.AlphabeticalCharacters ?? '';
      const whitespaceCharacters =
        item.whitespaceCharacters ?? item.WhitespaceCharacters ?? '';
      const shouldShowCountryFlags = analyzerName.toUpperCase().includes('CJK');
      return {
        id: String(analyzerId),
        title: analyzerName,
        content: (
          <AccordionContent
            description={description}
            options={[
              {
                label: 'Alphabetical Characters',
                value: getDisplayValue(alphabeticalCharacters),
              },
              {
                label: 'Whitespace Characters',
                value: getDisplayValue(whitespaceCharacters),
              },
            ]}
            dataTestId={`${index}`}
          />
        ),
        headerRightContent: shouldShowCountryFlags
          ? renderAccordionIcons()
          : undefined,
      };
    });
  }, [analyzerResponse]);

  const defaultExpandedSectionId = useMemo(() => {
    const consilioStandardTextSection = accordionSections.find(
      (section) =>
        typeof section.title === 'string' &&
        section.title.trim().toLowerCase() === 'consilio standard text'
    );
    return consilioStandardTextSection?.id ?? accordionSections[0]?.id ?? '1';
  }, [accordionSections]);

  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('indexManagement.customIndex.selectAnalyzer.analyzerTitle')}
      side="right"
      showButton
      size="small"
      className="analyzer-details-flyout"
    >
      <Box
        className="analyzer-details-flyout__content"
        aria-label="analyzer-details-flyout-content"
      >
        <Box className="analyzer-details-flyout__content-description">
          <Typography
            tabIndex={0}
            className="typo-text body3_size description-text"
            data-testid={`analyzer-details-flyout-analyzer-description`}
          >
            {t(
              'indexManagement.customIndex.selectAnalyzer.analyzerDescription'
            )}
          </Typography>
        </Box>
        <ArrAccordion
          key={`${isOpen}-${defaultExpandedSectionId}`}
          sections={accordionSections}
          expandedSection={defaultExpandedSectionId}
          isCustomSearchAccordian
          className="select-analyzer-accordion"
          dataTestId="select-analyzer-flyout"
          allowMultiple
        />
      </Box>
    </ArrDrawer>
  );
};

export default AnalyzerDetailsFlyout;
